import { Card, Grid } from "@mui/material";
import { ValidationError } from "class-validator";
import { useMemo, useRef, useState } from "react";
import { AddTaskMenuButton, TaskContainer } from "..";
import { useTaskContext } from "../../hooks";
import { Task } from "../../types";
import { TaskBody } from "../task-types";

import HighlightVariables from "../../../../components/layout/HighlightVariable";
import { Process } from "../../../../model/Process";
import { ButtonType } from "./AddTaskMenuButton";

const TaskInlineEditorContainer = ({
  task,
  validationFailures,
  stepOrProcessId,
  process,
}: {
  task: Task;
  validationFailures?: ValidationError[];
  stepOrProcessId: string;
  process: Process;
}) => {
  const taskContext = useTaskContext();
  const tasks = taskContext.getTasks(stepOrProcessId);
  const [taskMenuOpen, setTaskMenuOpen] = useState(false);
  const [raised, setRaised] = useState(false);
  const [addTaskButtonAbove, setAddTaskButtonAbove] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const hasRequestTracker = useMemo(() => {
    return !!task.data?.requestTracking;
  }, [task.data]);

  const notifyProcessOwner = useMemo(() => {
    return !!task.data?.notifyProcessOwner;
  }, [task.data]);

  return (
    <Grid
      container
      flexDirection={"column"}
      position={"relative"}
      zIndex={0}
      onMouseEnter={() => {
        setRaised(true);
      }}
      onMouseLeave={() => {
        setRaised(false);
        setTaskMenuOpen(false);
      }}
      onMouseMove={(e) => {
        if (!containerRef.current || taskMenuOpen) {
          return;
        }
        const bounds = containerRef.current.getBoundingClientRect();
        const y = e.clientY - bounds.top;
        const height = containerRef.current.clientHeight;
        setAddTaskButtonAbove(y < height / 2);
      }}
      ref={containerRef}
    >
      <Grid item>
        <Card
          sx={(theme) => {
            return {
              cursor: "pointer",
              mb: 2,
              zIndex: raised ? 1 : 10,
              outline:
                taskContext.selectedTask?.id == task.id
                  ? "1px solid " + theme.palette.action.active
                  : "",
            };
          }}
          elevation={raised || taskContext.selectedTask?.id == task.id ? 3 : 0}
          onClick={() => {
            taskContext.setSelectedTask(task);
          }}
        >
          <TaskContainer
            label={<HighlightVariables text={task.name} />}
            description={task.description}
            type={task.type}
            role={task.role}
            isComplete={false}
            showChildren={true}
            onClick={() => {
              // Do nothing
            }}
            validationFailures={validationFailures?.filter(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (failure) => (failure?.target as any)?.id == task.id,
            )}
            requestTracking={hasRequestTracker}
            notifyProcessOwner={notifyProcessOwner}
          >
            <TaskBody variant="inline" task={task} />
          </TaskContainer>
        </Card>
      </Grid>

      {raised && (
        <Grid
          item
          alignSelf={"center"}
          position={"absolute"}
          top={addTaskButtonAbove ? -10 : "unset"}
          bottom={addTaskButtonAbove ? "unset" : 5}
          zIndex={40}
        >
          <AddTaskMenuButton
            open={taskMenuOpen}
            setOpen={(value) => {
              setTaskMenuOpen(value);
              if (value === false) setRaised(false);
            }}
            variant={ButtonType.CARD}
            previousTask={
              addTaskButtonAbove
                ? tasks.find((item) => item.id === task.previous_task_id)
                : task
            }
            process={process}
            task={task}
            stepOrProcessId={stepOrProcessId}
            insertAbove={addTaskButtonAbove}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default TaskInlineEditorContainer;
